import { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, makeStyles, mergeClasses } from '@fluentui/react-components';
import { ChatHelp20Regular, Dismiss16Regular } from '@fluentui/react-icons';
import { useAuthStore } from '../stores/authStore.js';
import { publicDemoProductUrl } from '../lib/publicDemo.js';
import { useDemoNavStore } from '../demo/demoNavStore.js';
import { COACH_TIPS, resolveCoachScene, type CoachScene } from '../demo/demoCoachContent.js';
import { useThemeStore, resolveTheme } from '../stores/themeStore.js';

const useStyles = makeStyles({
  wrap: {
    background: 'rgba(12, 111, 214, 0.12)',
    borderBottom: '1px solid rgba(12, 111, 214, 0.35)',
    padding: '10px 24px',
  },
  /** Solo si se monta fuera del chrome del topbar (evitar: se tapa al hacer scroll). */
  wrapStickyLegacy: {
    position: 'sticky',
    top: 'var(--topbar-h)',
    zIndex: 95,
  },
  row: {
    maxWidth: '1440px',
    margin: '0 auto',
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '12px 20px',
    justifyContent: 'space-between',
  },
  copy: {
    fontSize: 'var(--fs-200)',
    color: 'var(--text-primary)',
    lineHeight: 1.4,
    flex: '1 1 220px',
  },
  steps: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: '6px',
    listStyle: 'none',
    margin: 0,
    padding: 0,
    flex: '2 1 360px',
  },
  step: {
    fontSize: '12px',
    padding: '4px 10px',
    borderRadius: '999px',
    border: '1px solid var(--border-subtle)',
    color: 'var(--text-secondary)',
    background: 'var(--bg-page)',
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    cursor: 'pointer',
  },
  stepActive: {
    border: '1px solid rgba(12, 111, 214, 0.55)',
    color: 'var(--opus-brand-60, #0c6fd6)',
    fontWeight: 600,
    background: 'rgba(12, 111, 214, 0.12)',
  },
  stepDone: {
    opacity: 0.75,
  },
  stepDisabled: {
    opacity: 0.45,
    cursor: 'default',
    pointerEvents: 'none',
  },
  cta: { flexShrink: 0 },
  coachToggle: { flexShrink: 0 },
  coachToggleActive: {
    border: '1px solid rgba(12, 111, 214, 0.55)',
  },
  /**
   * Mismas dimensiones que el coach flotante original (abajo a la derecha),
   * anclado arriba-derecha bajo el chrome sticky. Vidrio semitransparente.
   */
  card: {
    position: 'fixed',
    right: '20px',
    top: 'calc(var(--topbar-h) + 56px)',
    zIndex: 120,
    width: 'min(400px, calc(100vw - 32px))',
    maxHeight: 'min(70vh, 560px)',
    borderRadius: '12px',
    boxShadow: '0 16px 48px rgba(0,0,0,0.22)',
    padding: '14px 16px 16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    border: '1px solid transparent',
    backdropFilter: 'blur(18px) saturate(1.35)',
    // Fluent/Griffel: prefijo webkit vía propiedad estándar + fallback sólido abajo
  },
  cardOnDarkUi: {
    background: 'rgba(244, 247, 251, 0.78)',
    color: '#1a2332',
    border: '1px solid rgba(12, 111, 214, 0.4)',
  },
  cardOnLightUi: {
    background: 'rgba(21, 32, 51, 0.78)',
    color: '#e8eef7',
    border: '1px solid rgba(120, 180, 255, 0.4)',
  },
  scroll: {
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '10px',
    paddingRight: '4px',
    minHeight: 0,
  },
  head: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '8px',
    flexShrink: 0,
  },
  eyebrow: {
    fontSize: '11px',
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    marginBottom: '2px',
  },
  eyebrowOnLightCard: { color: '#0c6fd6' },
  eyebrowOnDarkCard: { color: '#7eb6ff' },
  title: {
    fontSize: 'var(--fs-300)',
    fontWeight: 650,
    margin: 0,
    lineHeight: 1.3,
  },
  intro: {
    fontSize: 'var(--fs-200)',
    lineHeight: 1.45,
    margin: 0,
    opacity: 0.9,
  },
  list: {
    margin: 0,
    paddingLeft: '1.15rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  item: {
    fontSize: '12.5px',
    lineHeight: 1.45,
  },
  next: {
    fontSize: '12px',
    borderRadius: '8px',
    padding: '8px 10px',
    margin: 0,
    lineHeight: 1.4,
    flexShrink: 0,
    textDecoration: 'none',
    display: 'block',
    cursor: 'default',
  },
  nextLink: {
    cursor: 'pointer',
    transitionProperty: 'filter, transform',
    transitionDuration: '120ms',
    ':hover': {
      filter: 'brightness(1.06)',
      transform: 'translateY(-1px)',
    },
  },
  nextOnDarkUi: {
    background: 'rgba(12, 111, 214, 0.14)',
    color: '#1a2332',
  },
  nextOnLightUi: {
    background: 'rgba(126, 182, 255, 0.18)',
    color: '#e8eef7',
  },
  dismiss: {
    minWidth: '28px',
    height: '28px',
  },
});

type Step = {
  id: string;
  label: string;
  match: (path: string) => boolean;
  to?: string | null;
};

type Props = {
  /**
   * true = va debajo del AppTopBar dentro del mismo bloque sticky
   * (recomendado). false = sticky propio (legacy; se tapa con el topbar).
   */
  embedded?: boolean;
};

const COACH_OPEN_KEY = 'opus_demo_coach_header_open';

/**
 * Guía del sitio demo: pills en el banner + asistente (botón en header,
 * panel flotante a la derecha, tamaño original).
 */
export function DemoGuide({ embedded = false }: Props) {
  const styles = useStyles();
  const location = useLocation();
  const demoGuest = useAuthStore((s) => s.demoGuest);
  const productUrl = useAuthStore((s) => s.demoProductUrl) || publicDemoProductUrl();
  const themeMode = useThemeStore((s) => s.mode);
  const uiIsDark = resolveTheme(themeMode) === 'dark';

  const ensureLoaded = useDemoNavStore((s) => s.ensureLoaded);
  const projectId = useDemoNavStore((s) => s.projectId);
  const quoteId = useDemoNavStore((s) => s.quoteId);
  const apuRunId = useDemoNavStore((s) => s.apuRunId);

  const [coachOpen, setCoachOpen] = useState(() => {
    try {
      const v = sessionStorage.getItem(COACH_OPEN_KEY);
      return v === null ? true : v === '1';
    } catch {
      return true;
    }
  });

  useEffect(() => {
    void ensureLoaded();
  }, [ensureLoaded]);

  const tip = useMemo(() => {
    const scene = resolveCoachScene(location.pathname);
    return COACH_TIPS[scene];
  }, [location.pathname]);

  // Al cambiar de vista, reabrir el tip (sigue siendo útil en el recorrido).
  useEffect(() => {
    setCoachOpen(true);
    try {
      sessionStorage.setItem(COACH_OPEN_KEY, '1');
    } catch {
      /* ignore */
    }
  }, [tip.scene]);

  const steps: Step[] = useMemo(() => {
    const workspaceTo = projectId ? `/project/${projectId}` : null;
    const quoteTo = projectId && quoteId ? `/project/${projectId}/quotes/${quoteId}` : null;
    const contextTo = projectId ? `/project/${projectId}/obra-context` : null;
    // Recorrido de 3 pasos (Proyecto+Documentos fusionados). El contexto va ANTES del
    // presupuesto (Sebastián, 2026-08-14): es el orden real de trabajo —primero dices
    // dónde y cómo es la obra, después pones precios—, y así el recorrido no tiene que
    // mandar al visitante de vuelta a una pantalla que ya pasó.
    //
    // ERA DE 4 PASOS: el cuarto, "Análisis con IA", llevaba a una pantalla aparte que ya
    // no se usa —hoy los análisis se generan y se revisan DENTRO del presupuesto—. Se
    // retiró en vez de reapuntarlo: mandar al visitante a otra dirección para ver algo
    // que está en la pantalla donde ya estaba enseña el producto al revés. El paso 3 lo
    // explica en su propio texto.
    return [
      {
        id: 'project_docs',
        label: '1 · Proyecto y documentos',
        match: (p) => p === '/projects' || p === '/' || /^\/project\/[^/]+$/.test(p),
        to: workspaceTo ?? '/projects',
      },
      {
        id: 'context',
        label: '2 · Contexto de proyecto',
        match: (p) => p.includes('/obra-context'),
        to: contextTo,
      },
      {
        id: 'quote',
        label: '3 · Presupuesto y análisis con IA',
        // Se reconoce también estando en la pantalla vieja: si alguien llega ahí por la
        // dirección, el recorrido no se queda sin paso activo.
        match: (p) => /\/quotes\/[^/]+/.test(p),
        to: quoteTo,
      },
    ];
  }, [projectId, quoteId]);

  if (!demoGuest) return null;

  const path = location.pathname;
  const activeIdx = (() => {
    for (let i = steps.length - 1; i >= 0; i--) {
      if (steps[i]!.match(path)) return i;
    }
    return -1;
  })();

  function setOpen(next: boolean) {
    setCoachOpen(next);
    try {
      sessionStorage.setItem(COACH_OPEN_KEY, next ? '1' : '0');
    } catch {
      /* ignore */
    }
  }

  function pathForScene(scene: CoachScene | undefined): string | null {
    if (!scene) return null;
    if (scene === 'projects' || scene === 'other') return '/projects';
    if (!projectId) return null;
    if (scene === 'workspace') return `/project/${projectId}`;
    if (scene === 'context') return `/project/${projectId}/obra-context`;
    if (scene === 'quote') {
      return quoteId ? `/project/${projectId}/quotes/${quoteId}` : null;
    }
    if (scene === 'apu') {
      if (projectId && quoteId && apuRunId) {
        return `/project/${projectId}/quotes/${quoteId}/apu-ia/runs/${apuRunId}`;
      }
      // Sin run aún: listado de corridas APU (no el presupuesto genérico).
      return quoteId ? `/project/${projectId}/quotes/${quoteId}/apu-ia` : null;
    }
    return null;
  }

  const nextTo = pathForScene(tip.nextScene);

  return (
    <>
      <div
        className={mergeClasses(styles.wrap, !embedded && styles.wrapStickyLegacy)}
        role="region"
        aria-label="Guía del sitio de demostración"
      >
        <div className={styles.row}>
          <div className={styles.copy}>
            <strong>OPUS ai · datos de muestra de Ecosoft · solo lectura.</strong>{' '}
            Recorre el concurso de muestra ya procesado — no se consumen créditos.
          </div>
          <ol className={styles.steps}>
            {steps.map((s, i) => {
              const active = i === activeIdx;
              const done = activeIdx > i;
              const className = [
                styles.step,
                active ? styles.stepActive : '',
                done ? styles.stepDone : '',
                !s.to ? styles.stepDisabled : '',
              ]
                .filter(Boolean)
                .join(' ');
              if (s.to) {
                return (
                  <li key={s.id}>
                    <Link to={s.to} className={className}>
                      {s.label}
                    </Link>
                  </li>
                );
              }
              return (
                <li key={s.id}>
                  <span className={className}>{s.label}</span>
                </li>
              );
            })}
          </ol>
          <Button
            className={mergeClasses(styles.coachToggle, coachOpen && styles.coachToggleActive)}
            appearance="secondary"
            icon={<ChatHelp20Regular />}
            onClick={() => setOpen(!coachOpen)}
          >
            Asistente demo
          </Button>
          <Button
            className={styles.cta}
            appearance="primary"
            onClick={() => window.open(productUrl, '_blank', 'noopener,noreferrer')}
          >
            Activar Start
          </Button>
        </div>
      </div>

      {coachOpen && (
        <aside
          className={mergeClasses(
            styles.card,
            uiIsDark ? styles.cardOnDarkUi : styles.cardOnLightUi,
          )}
          aria-label="Asistente de demostración"
        >
          <div className={styles.head}>
            <div>
              <div
                className={mergeClasses(
                  styles.eyebrow,
                  uiIsDark ? styles.eyebrowOnLightCard : styles.eyebrowOnDarkCard,
                )}
              >
                Asistente · demo
              </div>
              <h2 className={styles.title}>{tip.title}</h2>
            </div>
            <Button
              className={styles.dismiss}
              appearance="subtle"
              icon={<Dismiss16Regular />}
              aria-label="Cerrar asistente"
              onClick={() => setOpen(false)}
            />
          </div>
          <div className={styles.scroll}>
            <p className={styles.intro}>{tip.intro}</p>
            <ol className={styles.list}>
              {tip.bullets.map((b) => (
                <li key={b.slice(0, 48)} className={styles.item}>
                  {b}
                </li>
              ))}
            </ol>
          </div>
          {tip.nextHint && (
            nextTo ? (
              <Link
                to={nextTo}
                className={mergeClasses(
                  styles.next,
                  styles.nextLink,
                  uiIsDark ? styles.nextOnDarkUi : styles.nextOnLightUi,
                )}
              >
                <strong>Siguiente:</strong> {tip.nextHint}
              </Link>
            ) : (
              <p
                className={mergeClasses(
                  styles.next,
                  uiIsDark ? styles.nextOnDarkUi : styles.nextOnLightUi,
                )}
              >
                <strong>Siguiente:</strong> {tip.nextHint}
              </p>
            )
          )}
        </aside>
      )}
    </>
  );
}
